import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { ClassAttributes, createElement, MouseEvent, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { SingleCreationForDetailFragment } from '../../../../graphql/generated/graphql';
import { ProjectType } from '../../../../service/persistent/ProjectWebApi';
import { getLatestDeliverable } from '../../../../utility/graphql/creation';
import { toJSDate } from '../../../../utility/graphql/datetime';
import Grid from '../../../component/primitive/Grid';
import { SPACING_UNIT } from '../../../constant/spacing';
import CreationsDummyItem from './CreationsDummyItem';
import CreationsItem from './CreationsItem';

type Props = ClassAttributes<HTMLElement> & {
  myCreations: Pick<
    SingleCreationForDetailFragment,
    'id' | 'deliverables' | 'state' | 'assignee' | 'creativeFormat' | 'createdAt'
  >[];
  otherCreations: Pick<
    SingleCreationForDetailFragment,
    'id' | 'deliverables' | 'state' | 'assignee' | 'creativeFormat' | 'createdAt'
  >[];
  project: Pick<ProjectType, 'id'> | null;
  loading: boolean;
  onClickSome: (e: MouseEvent<HTMLElement>, creationId: string) => void;
  onClickProfile: (url: string) => void;
  className?: string;
  profileWebUrl: string;
};

const Creations = ({
  myCreations,
  otherCreations,
  project,
  loading,
  className,
  onClickSome,
  onClickProfile,
  profileWebUrl,
}: Props) => {
  const filteredMyCreations = myCreations.filter(
    (creation) => creation.state !== 'canceled' && getLatestDeliverable(creation.deliverables),
  );
  const filteredOtherCreations = otherCreations.filter(
    (creation) => creation.state !== 'canceled' && getLatestDeliverable(creation.deliverables),
  );

  let myItems: ReactElement<any>[];
  let otherItems: ReactElement<any>[];

  if (loading) {
    myItems = otherItems = [
      <CreationsDummyItem key="dummy0" />,
      <CreationsDummyItem key="dummy1" />,
      <CreationsDummyItem key="dummy2" />,
    ];
  } else {
    if (filteredMyCreations.length === 0 && filteredOtherCreations.length === 0) {
      return null;
    }

    myItems = filteredMyCreations
      .sort((a, b) => toJSDate(a.createdAt).valueOf() - toJSDate(b.createdAt).valueOf())
      .map((creation) => (
        <CreationsItem
          creation={creation}
          onClickSome={project ? (e) => onClickSome(e, creation.id) : () => {}}
          key={creation.id}
        />
      ));

    otherItems = filteredOtherCreations
      .sort((a, b) => toJSDate(a.createdAt).valueOf() - toJSDate(b.createdAt).valueOf())
      .map((creation) => (
        <CreationsItem
          creation={creation}
          key={creation.id}
          onClickProfile={onClickProfile}
          profileWebUrl={profileWebUrl}
        />
      ));
  }

  return (
    <Root className={className}>
      <Heading variant="h5">
        <FormattedMessage id="project_detail_page.creations.heading" />
      </Heading>

      {myItems.length > 0 ? (
        <div>
          <SubHeading variant="subtitle1">
            <FormattedMessage id="project_detail_page.creations.subheading.your_creatives" />
          </SubHeading>

          <Items columns={3}>{myItems}</Items>
        </div>
      ) : null}

      {otherItems.length > 0 ? (
        <div>
          <SubHeading variant="subtitle1">
            <FormattedMessage id="project_detail_page.creations.subheading.other_creatives" />
          </SubHeading>

          <Items columns={3}>{otherItems}</Items>
        </div>
      ) : null}
    </Root>
  );
};

const Root = styled('div')();

const Heading = styled(Typography)({ marginBottom: SPACING_UNIT * 3 });

const SubHeading = styled(Typography)({ marginBottom: SPACING_UNIT * 2 });

const Items = styled(Grid)({
  marginBottom: SPACING_UNIT * 5,
});

export default Creations;
